import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Landmark, Wallet } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { parseApiError } from "../../lib/api";
import {
  normalizeBankDetails,
  validateBankDetails,
} from "../../lib/bankDetailsValidation";
import { cn } from "../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Label = ({
  htmlFor,
  children
}) => {
  usePartnerTranslation();
  return <label htmlFor={htmlFor} className="text-sm font-semibold text-[#0F1115]">
    {children}
  </label>;
};

const METHOD_OPTIONS = [
  {
    value: "bank_transfer",
    get label() { return tr("Bank transfer"); },
    get description() { return tr("Payouts go straight to your bank account via SEPA or SWIFT."); },
    Icon: Landmark,
  },
  {
    value: "paypal",
    get label() { return tr("PayPal"); },
    get description() { return tr("Payouts arrive in your PayPal balance — usually within hours."); },
    Icon: Wallet,
  },
];

const MethodRadio = ({
  value,
  onChange
}) => {
  usePartnerTranslation();
  return <div role="radiogroup" aria-label={tr("Payment method")} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
    {METHOD_OPTIONS.map(opt => {
      const active = opt.value === value;
      const Icon = opt.Icon;
      return <button key={opt.value} type="button" role="radio" aria-checked={active} data-testid={`pm-method-${opt.value}`} onClick={() => onChange(opt.value)} className={cn("flex items-start gap-3 rounded-xl border p-4 text-start transition-all", active ? "border-[#FF5757] bg-[#FFF4F4]" : "border-[#E6E8EC] bg-white hover:border-[#0F1115]")}>
          <span className={cn("mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg", active ? "bg-[#FF5757]/15 text-[#FF5757]" : "bg-[#F3EEEE] text-[#6B7280]")}>
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-extrabold text-[#0F1115]">
                {opt.label}
              </span>
              <span className={cn("inline-flex h-4 w-4 items-center justify-center rounded-full border transition-colors", active ? "border-[#FF5757] bg-[#FF5757]" : "border-[#D7DAE0] bg-white")}>
                {active && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </span>
            </div>
            <p className="mt-1 text-xs text-[#6B7280]">{opt.description}</p>
          </div>
        </button>;
    })}
  </div>;
};

const BANK_FIELDS = [
  { key: "iban", label: "IBAN", get placeholder() { return tr("e.g. GB33 BUKB 2020 1555 5555 55"); }, maxLength: 42, uppercase: true, autoComplete: "off" },
  { key: "swift", label: "SWIFT / BIC", get placeholder() { return tr("e.g. BUKBGB22"); }, maxLength: 11, uppercase: true, autoComplete: "off" },
  { key: "account_type", get label() { return tr("Account type"); }, get placeholder() { return tr("e.g. Business checking"); }, maxLength: 120 },
  { key: "beneficiary_name", get label() { return tr("Beneficiary name"); }, get placeholder() { return tr("e.g. Acme Tours Ltd"); }, maxLength: 160 },
  { key: "bank_city", get label() { return tr("Bank city"); }, get placeholder() { return tr("e.g. London"); }, maxLength: 120 },
  { key: "bank_name", get label() { return tr("Bank name"); }, get placeholder() { return tr("e.g. Barclays Bank"); }, maxLength: 160 },
  { key: "bank_address", get label() { return tr("Bank address"); }, get placeholder() { return tr("e.g. 1 Churchill Place"); }, maxLength: 300, fullWidth: true },
  { key: "bank_zip", get label() { return tr("Postal / ZIP code"); }, get placeholder() { return tr("e.g. E14 5HP"); }, maxLength: 20, uppercase: true },
];

const PaymentMethodEditModal = ({
  open,
  initial,
  otpEmail,
  otpCode,
  onClose,
  onSaved,
}) => {
  usePartnerTranslation();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      // Masked identifiers ("••••1234") are display-only; the partner re-enters them to change details.
      setForm(Object.fromEntries(
        Object.entries(initial || {}).map(([key, value]) => [key, typeof value === "string" && value.startsWith("••••") ? "" : value]),
      ));
      setErrors({});
    }
  }, [open, initial]);

  if (!open) return null;

  const method = form.method || "bank_transfer";
  const setMethod = (m) => setForm((f) => ({ ...f, method: m }));
  const paypalInvalid =
    method === "paypal" && form.paypal_email
      ? !EMAIL_RE.test(form.paypal_email.trim())
      : false;

  const submit = async () => {
    if (!otpCode) {
      toast.error(tr("Verification expired — please try again."));
      onClose();
      return;
    }
    if (method === "paypal" && !form.paypal_email?.trim()) {
      toast.error(tr("Please enter your PayPal email."));
      return;
    }
    if (paypalInvalid) return;

    const bankErrors = method === "bank_transfer" ? validateBankDetails(form) : {};
    setErrors(bankErrors);
    if (Object.keys(bankErrors).length > 0) {
      toast.error(tr("Check your bank details"), {
        description: Object.values(bankErrors)[0],
      });
      return;
    }

    setSaving(true);
    try {
      const bank = normalizeBankDetails(form);
      const payload = {
        otp_email: otpEmail,
        otp_code: otpCode,
        method,
        // Always send both sets; the backend keeps data for the inactive
        // method so switching back doesn't lose it.
        ...bank,
        paypal_email: (form.paypal_email || "").trim(),
      };
      const res = await axios.put(`${API}/finance/me/payment-method`, payload);
      toast.success(tr("Payment method saved"));
      onSaved(res.data.payment_method);
    } catch (err) {
      const { message } = parseApiError(err, tr("Please try again."));
      toast.error(tr("Couldn't save payment method"), {
        description: message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="payment-edit-modal"
        className="max-h-[92vh] max-w-[640px] overflow-y-auto rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-5">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Edit payment method")}</DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">{tr("Pick how you'd like to receive payouts, then fill in the details.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 px-6 py-6">
          <div>
            <Label>{tr("Payment method")}</Label>
            <div className="mt-2">
              <MethodRadio value={method} onChange={setMethod} />
            </div>
          </div>

          {method === "bank_transfer" ? (
            <div
              data-testid="pm-bank-fields"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              {BANK_FIELDS.map((f) => (
                <div key={f.key} className={f.fullWidth ? "sm:col-span-2" : ""}>
                  <Label htmlFor={`pm-${f.key}`}>{f.label}</Label>
                  <input
                    id={`pm-${f.key}`}
                    data-testid={`pm-${f.key}`}
                    type="text"
                    value={form[f.key] || ""}
                    placeholder={f.placeholder}
                    maxLength={f.maxLength || 120}
                    autoComplete={f.autoComplete}
                    aria-invalid={Boolean(errors[f.key])}
                    aria-describedby={errors[f.key] ? `pm-${f.key}-error` : undefined}
                    onChange={(e) =>
                      {
                        const value = f.uppercase
                          ? e.target.value.toUpperCase()
                          : e.target.value;
                        setForm((prev) => ({ ...prev, [f.key]: value }));
                        setErrors((prev) => ({ ...prev, [f.key]: undefined }));
                      }
                    }
                    className={cn(
                      "humrahe-input mt-2",
                      errors[f.key] && "border-[#9B0000] focus:border-[#9B0000]",
                    )}
                  />
                  {errors[f.key] && (
                    <p id={`pm-${f.key}-error`} className="mt-1 text-xs font-semibold text-[#9B0000]">
                      {errors[f.key]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div data-testid="pm-paypal-fields">
              <Label htmlFor="pm-paypal-email">{tr("PayPal email")}</Label>
              <input
                id="pm-paypal-email"
                data-testid="pm-paypal_email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={form.paypal_email || ""}
                maxLength={120}
                placeholder="payouts@yourcompany.com"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, paypal_email: e.target.value }))
                }
                className={cn(
                  "humrahe-input mt-2",
                  paypalInvalid &&
                    "border-[#9B0000] focus:border-[#9B0000] focus:shadow-[0_0_0_4px_rgba(155,0,0,0.12)]",
                )}
              />
              {paypalInvalid ? (
                <p
                  data-testid="pm-paypal-error"
                  className="mt-2 text-xs font-semibold text-[#9B0000]"
                >{tr("Please enter a valid email address.")}</p>
              ) : (
                <p className="mt-2 text-xs text-[#6B7280]">{tr("Use the email on the PayPal account where you'd like to receive payouts.")}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#EFE4E4] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="pm-edit-cancel"
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
          >{tr("Cancel")}</button>
          <button
            type="button"
            data-testid="pm-edit-save"
            onClick={submit}
            disabled={saving || paypalInvalid}
            className="humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Saving…")}</>
            ) : (
              tr("Save changes")
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodEditModal;
